import type { Log, Metric, ReplayEvent, TransactionEvent } from "@sentry/core";
import type { ErrorEvent, EventHint } from "@sentry/nextjs";
import { isSentinelValue } from "@/lib/env";

const SENSITIVE_KEY_PATTERN = /authorization|api[_-]?key|token|password|secret|cookie|stripe|workos|prompt|completion/i;

const LOG_LEVEL_RANK: Record<string, number> = {
  debug: 10,
  info: 20,
  warning: 30,
  warn: 30,
  error: 40,
  fatal: 50,
};

const scrubValue = (value: unknown): unknown => {
  // Only object keys are matched against SENSITIVE_KEY_PATTERN. Applying it to
  // string values as well strips useful error context (e.g. LLM messages that
  // mention "token", "prompt", or "completion") without protecting anything the
  // key match does not already cover.
  if (typeof value === "string") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(scrubValue);
  }

  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    const scrubbed: Record<string, unknown> = {};
    for (const [key, nested] of Object.entries(record)) {
      if (SENSITIVE_KEY_PATTERN.test(key)) {
        scrubbed[key] = "[Filtered]";
      } else {
        scrubbed[key] = scrubValue(nested);
      }
    }
    return scrubbed;
  }

  return value;
};

const stripQueryFromUrl = (url: string): string => {
  const queryIndex = url.indexOf("?");
  return queryIndex === -1 ? url : url.slice(0, queryIndex);
};

const QUERY_BEARING_URL_KEYS = ["url", "http.url", "url.full", "from", "to"] as const;
const QUERY_STRING_KEYS = ["http.query", "url.query"] as const;

const scrubQueryBearingFields = (record: { [key: string]: unknown } | undefined): void => {
  if (!record) {
    return;
  }

  for (const key of QUERY_BEARING_URL_KEYS) {
    const value = record[key];
    if (typeof value === "string") {
      record[key] = stripQueryFromUrl(value);
    }
  }

  for (const key of QUERY_STRING_KEYS) {
    if (key in record) {
      delete record[key];
    }
  }
};

const scrubEventQueryStrings = (event: ErrorEvent | TransactionEvent | ReplayEvent): void => {
  if (event.request) {
    if (typeof event.request.url === "string") {
      event.request.url = stripQueryFromUrl(event.request.url);
    }
    delete event.request.query_string;
  }

  for (const breadcrumb of event.breadcrumbs ?? []) {
    scrubQueryBearingFields(breadcrumb.data);
  }

  for (const span of event.spans ?? []) {
    if (typeof span.description === "string") {
      span.description = stripQueryFromUrl(span.description);
    }
    scrubQueryBearingFields(span.data);
  }
};

const SENTRY_DISABLED_VALUES = new Set(["false", "0", "no", "off"]);

export const isSentryReportingEnabled = (): boolean => {
  // NEXT_PUBLIC_* is the only variant Next.js inlines into the browser bundle, so
  // it must win client-side. Server runtimes fall back to the plain var. An
  // unreplaced build-time sentinel is treated as unset.
  const publicEnabled = process.env.NEXT_PUBLIC_SENTRY_ENABLED;
  const rawFlag = publicEnabled && !isSentinelValue(publicEnabled) ? publicEnabled : process.env.SENTRY_ENABLED;
  const flag = (rawFlag ?? "").trim().toLowerCase();
  return !SENTRY_DISABLED_VALUES.has(flag);
};

export const isSentryEnabled = (): boolean => {
  if (!isSentryReportingEnabled()) {
    return false;
  }

  const dsn = process.env.SENTRY_DSN ?? process.env.NEXT_PUBLIC_SENTRY_DSN ?? "";
  if (!dsn || isSentinelValue(dsn)) {
    return false;
  }

  return true;
};

export const resolveSentryDsn = (): string | undefined => {
  const dsn = process.env.SENTRY_DSN ?? process.env.NEXT_PUBLIC_SENTRY_DSN;
  if (!dsn || isSentinelValue(dsn)) {
    return undefined;
  }
  return dsn;
};

export const parseSampleRate = (value: string | undefined, fallback: number): number => {
  const parsed = Number.parseFloat(value ?? "");
  return Number.isFinite(parsed) ? parsed : fallback;
};

const SENTRY_REPLAY_BLOCKED_PATHS = ["/login", "/signup", "/verify-email", "/logout"] as const;

export const isSentryReplayBlockedPath = (pathname: string): boolean =>
  SENTRY_REPLAY_BLOCKED_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));

const resolveMinLogLevel = (): number => {
  const publicLevel = process.env.NEXT_PUBLIC_SENTRY_LOGS_LEVEL;
  const rawLevel = publicLevel && !isSentinelValue(publicLevel) ? publicLevel : process.env.SENTRY_LOGS_LEVEL;
  const raw = (rawLevel ?? "info").trim().toLowerCase();
  return LOG_LEVEL_RANK[raw] ?? LOG_LEVEL_RANK.info;
};

export const beforeSend = (event: ErrorEvent, _hint: EventHint): ErrorEvent | null => {
  if (event.request?.headers) {
    event.request.headers = scrubValue(event.request.headers) as Record<string, string>;
  }

  if (event.request?.cookies) {
    event.request.cookies = scrubValue(event.request.cookies) as Record<string, string>;
  }

  if (event.extra) {
    event.extra = scrubValue(event.extra) as Record<string, unknown>;
  }

  scrubEventQueryStrings(event);
  return event;
};

export const beforeSendTransaction = (event: TransactionEvent, _hint: EventHint): TransactionEvent | null => {
  scrubEventQueryStrings(event);
  return event;
};

export const beforeSendReplay = (event: ReplayEvent, _hint: EventHint): ReplayEvent | null => {
  if (Array.isArray(event.urls)) {
    event.urls = event.urls.map(stripQueryFromUrl);
  }

  scrubEventQueryStrings(event);
  return event;
};

export const beforeSendLog = (log: Log): Log | null => {
  const minLevel = resolveMinLogLevel();
  const logLevel = LOG_LEVEL_RANK[(log.level ?? "info").toLowerCase()] ?? LOG_LEVEL_RANK.info;
  if (logLevel < minLevel) {
    return null;
  }

  if (log.attributes) {
    log.attributes = scrubValue(log.attributes) as Record<string, unknown>;
  }
  return log;
};

export const beforeSendMetric = (metric: Metric): Metric => {
  if (metric.attributes) {
    metric.attributes = scrubValue(metric.attributes) as Record<string, unknown>;
  }
  return metric;
};

const resolveTracePropagationTargets = (): Array<string | RegExp> => {
  const targets: Array<string | RegExp> = [
    "localhost",
    /^https?:\/\/localhost(?::\d+)?\/api\//,
    /^https?:\/\/127\.0\.0\.1(?::\d+)?\/api\//,
    /^\/api\//,
  ];

  const apiBase = process.env.NEXT_PUBLIC_API_BASE_URL;
  if (apiBase && !isSentinelValue(apiBase)) {
    targets.push(apiBase);
  }

  const graphqlEndpoint = process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT;
  if (graphqlEndpoint && !isSentinelValue(graphqlEndpoint)) {
    targets.push(graphqlEndpoint);
  }

  return targets;
};

const resolvePublicOrServerEnv = (publicKey: string, serverKey: string): string | undefined => {
  const publicValue = process.env[publicKey];
  const raw = publicValue && !isSentinelValue(publicValue) ? publicValue : process.env[serverKey];
  const trimmed = (raw ?? "").trim();
  return trimmed !== "" ? trimmed : undefined;
};

const resolveStaticSentryTags = (): Record<string, string> => {
  const tags: Record<string, string> = {
    api_surface: "ui",
  };

  const service = resolvePublicOrServerEnv("NEXT_PUBLIC_SENTRY_SERVICE", "SENTRY_SERVICE");
  if (service) {
    tags.service = service;
  }

  const machine = resolvePublicOrServerEnv("NEXT_PUBLIC_SENTRY_MACHINE", "SENTRY_MACHINE");
  if (machine) {
    tags.machine = machine;
  }

  const branch = resolvePublicOrServerEnv("NEXT_PUBLIC_SENTRY_BRANCH", "SENTRY_BRANCH");
  if (branch) {
    tags.branch = branch;
  }

  const imageTag =
    resolvePublicOrServerEnv("NEXT_PUBLIC_SENTRY_IMAGE_TAG", "SENTRY_IMAGE_TAG") ||
    resolvePublicOrServerEnv("NEXT_PUBLIC_SENTRY_RELEASE_VERSION", "SENTRY_RELEASE_VERSION") ||
    (() => {
      const release = resolvePublicOrServerEnv("NEXT_PUBLIC_SENTRY_RELEASE", "SENTRY_RELEASE");
      if (!release) return undefined;
      const at = release.lastIndexOf("@");
      return at >= 0 ? release.slice(at + 1) : release;
    })();
  if (imageTag) {
    tags.image_tag = imageTag;
  }

  return tags;
};

export const getSharedSentryOptions = () => {
  // Prefer NEXT_PUBLIC_* (the only variant Next.js inlines into the browser
  // bundle) and fall back to the plain server var for SSR/edge. Unreplaced
  // build-time sentinels are treated as unset. NEXT_PUBLIC_SENTRY_RELEASE is
  // baked at build time (no runtime sentinel), so it needs no sentinel guard.
  const publicEnvironment = process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT;
  const publicTracesSampleRate = process.env.NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE;
  const publicProfilesSampleRate = process.env.NEXT_PUBLIC_SENTRY_PROFILES_SAMPLE_RATE;
  const tags = resolveStaticSentryTags();

  return {
    dsn: resolveSentryDsn(),
    enabled: isSentryEnabled(),
    environment:
      publicEnvironment && !isSentinelValue(publicEnvironment) ? publicEnvironment : process.env.SENTRY_ENVIRONMENT,
    release: process.env.NEXT_PUBLIC_SENTRY_RELEASE || process.env.SENTRY_RELEASE || undefined,
    sendDefaultPii: false,
    tracesSampleRate: parseSampleRate(
      publicTracesSampleRate && !isSentinelValue(publicTracesSampleRate)
        ? publicTracesSampleRate
        : process.env.SENTRY_TRACES_SAMPLE_RATE,
      0.1,
    ),
    profilesSampleRate: parseSampleRate(
      publicProfilesSampleRate && !isSentinelValue(publicProfilesSampleRate)
        ? publicProfilesSampleRate
        : process.env.SENTRY_PROFILES_SAMPLE_RATE,
      0.1,
    ),
    enableLogs: true,
    enableMetrics: true,
    tracePropagationTargets: resolveTracePropagationTargets(),
    beforeSend,
    beforeSendTransaction,
    beforeSendLog,
    beforeSendMetric,
    initialScope: { tags },
  };
};
